// SophiaCalls.jsx — every phone call Sophia (AI voice) made or answered, with
// the recording and transcript (owner request 2026-11-02). Filter by
// direction, outcome and date; play any call right here.
// Data: voice_calls (written by supabase/functions/voice-caller). Audio is
// streamed through voice-caller?audio=… so the ElevenLabs key never reaches
// the browser.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";

const C = {
  card: "#ffffff", border: "#e8e4df", light: "#f6f3ef", text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af",
  gold: "#b48c3c", goldBg: "rgba(180,140,60,0.08)",
  green: "#2e8b57", greenL: "rgba(46,139,87,0.12)", blue: "#2f6fb3", blueL: "rgba(47,111,179,0.10)",
  amber: "#b8860b", amberL: "rgba(184,134,11,0.12)", red: "#c0392b", redL: "rgba(192,57,43,0.08)", grey: "#6b7280", greyL: "rgba(107,114,128,0.10)",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://fuoejykgpnfphrvqjuub.supabase.co";

const OUTCOMES = {
  booked: { label: "Booked", fg: C.green, bg: C.greenL },
  talked: { label: "Talked, not booked", fg: C.blue, bg: C.blueL },
  voicemail: { label: "Voicemail", fg: C.amber, bg: C.amberL },
  screened: { label: "Call screener", fg: C.amber, bg: C.amberL },
  no_answer: { label: "No answer", fg: C.grey, bg: C.greyL },
  hung_up: { label: "Hung up", fg: C.red, bg: C.redL },
  live: { label: "In progress", fg: C.gold, bg: C.goldBg },
};
const outcomeOf = (c) => c.outcome || (["dialing", "started", "inbound"].includes(c.status) ? "live" : "no_answer");
const RANGES = { today: "Today", "7": "Last 7 days", "30": "Last 30 days", all: "All" };

const fmtWhen = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—");
const fmtPhone = (p) => { const d = String(p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || ""; };

export default function SophiaCalls() {
  const [calls, setCalls] = useState(null);
  const [err, setErr] = useState("");
  const [dir, setDir] = useState("all");
  const [range, setRange] = useState("7");
  const [picked, setPicked] = useState(new Set()); // empty = all outcomes
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [token, setToken] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setToken(data?.session?.access_token || ""));
    const load = async () => {
      let qry = supabase.from("voice_calls").select("id,created_at,status,direction,outcome,first_name,phone,interest,duration_secs,conversation_id,transcript,lead_id").order("created_at", { ascending: false }).limit(500);
      if (range !== "all") {
        const since = range === "today" ? new Date(new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }) + "T00:00:00-05:00") : new Date(Date.now() - Number(range) * 86400000);
        qry = qry.gte("created_at", since.toISOString());
      }
      const { data, error } = await qry;
      if (error) { setErr(error.message); setCalls([]); return; }
      setErr(""); setCalls(data || []);
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [range]);

  const rows = useMemo(() => (calls || []).filter((c) => {
    const d = c.direction === "inbound" ? "in" : "out";
    if (dir !== "all" && d !== dir) return false;
    if (picked.size && !picked.has(outcomeOf(c))) return false;
    if (q && !`${c.first_name || ""} ${c.phone || ""} ${c.interest || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [calls, dir, picked, q]);

  const stats = useMemo(() => {
    const all = calls || [];
    const n = (o) => all.filter((c) => outcomeOf(c) === o).length;
    const connected = n("booked") + n("talked") + n("hung_up");
    return { total: all.length, connected, booked: n("booked"), voicemail: n("voicemail"), rate: connected ? Math.round((100 * n("booked")) / connected) : 0 };
  }, [calls]);

  const toggle = (o) => setPicked((p) => { const s = new Set(p); s.has(o) ? s.delete(o) : s.add(o); return s; });
  const chip = (active) => ({ padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: F, cursor: "pointer", border: `1px solid ${active ? C.gold : C.border}`, background: active ? C.goldBg : C.card, color: active ? C.gold : C.muted });

  return (
    <div style={{ fontFamily: F, color: C.text, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontFamily: S, fontSize: 24, fontWeight: 700, color: C.gold }}>Sophia Calls</div>
        <div style={{ fontSize: 12.5, color: C.muted }}>Every call Sophia made or answered, with the recording. Refreshes every 30 seconds.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10 }}>
        {[["Calls", stats.total], ["Picked up", stats.connected], ["Booked", stats.booked], ["Booking rate", `${stats.rate}%`], ["Voicemails", stats.voicemail]].map(([k, v]) => (
          <div key={k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: C.muted, fontWeight: 600 }}>{k}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: k === "Booked" ? C.green : C.text }}>{calls ? v : "…"}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {[["all", "All calls"], ["out", "Sophia called"], ["in", "Called in"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setDir(k)} style={chip(dir === k)}>{l}</button>
        ))}
        <span style={{ width: 1, height: 22, background: C.border, margin: "0 4px" }} />
        {Object.entries(OUTCOMES).map(([k, o]) => (
          <button key={k} type="button" onClick={() => toggle(k)} style={chip(picked.has(k))}>{o.label}</button>
        ))}
        <span style={{ width: 1, height: 22, background: C.border, margin: "0 4px" }} />
        <select id="sophia-calls-range" value={range} onChange={(e) => setRange(e.target.value)} style={{ ...chip(false), paddingRight: 8 }}>
          {Object.entries(RANGES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input id="sophia-calls-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, treatment" style={{ flex: "1 1 200px", minWidth: 0, padding: "7px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: F, fontSize: 13 }} />
      </div>

      {err && <div style={{ padding: 12, background: C.redL, color: C.red, borderRadius: 8, fontSize: 13 }}>Couldn't load calls: {err}</div>}
      {calls && rows.length === 0 && !err && <div style={{ padding: 30, textAlign: "center", color: C.muted, background: C.light, borderRadius: 10, fontSize: 13 }}>No calls match these filters yet.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((c) => {
          const o = OUTCOMES[outcomeOf(c)];
          const isOpen = open === c.id;
          const inbound = c.direction === "inbound";
          return (
            <div key={c.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: inbound ? C.blue : C.gold, minWidth: 74 }}>{inbound ? "↙ Called in" : "↗ Sophia called"}</span>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{c.first_name || "Unknown"} <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}>{fmtPhone(c.phone)}</span></div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>{fmtWhen(c.created_at)} · {c.interest || "—"} · {fmtDur(c.duration_secs)}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, color: o.fg, background: o.bg }}>{o.label}</span>
                {c.conversation_id && c.duration_secs > 0 && (
                  <button type="button" onClick={() => setOpen(isOpen ? null : c.id)} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: isOpen ? C.goldBg : C.card, color: C.gold, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: F }}>
                    {isOpen ? "Close" : "▶ Listen"}
                  </button>
                )}
              </div>
              {isOpen && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  <audio controls autoPlay preload="none" style={{ width: "100%" }} src={`${SUPABASE_URL}/functions/v1/voice-caller?audio=${encodeURIComponent(c.conversation_id)}&t=${encodeURIComponent(token)}`} />
                  {Array.isArray(c.transcript) && c.transcript.length > 0 && (
                    <div style={{ background: C.light, borderRadius: 8, padding: "10px 12px", maxHeight: 320, overflowY: "auto", fontSize: 12.5, lineHeight: 1.55 }}>
                      {c.transcript.filter((t) => t.message).map((t, i) => (
                        <div key={i}><b style={{ color: t.role === "agent" ? C.gold : C.blue }}>{t.role === "agent" ? "Sophia" : (c.first_name || "Caller")}:</b> {String(t.message).replace(/\[(long pause|laughs)\]\s*/g, "")}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
